import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/** Standard right-mounted slide-in panel shared by the order modals. */
export function SidePanel({
  onClose,
  width,
  zIndex,
  children,
}: {
  onClose: () => void;
  /** Tailwind width for desktop, e.g. "md:w-[560px]". Mobile is full-width. */
  width: string;
  zIndex: number;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  // Portal to <body>: ancestors with transforms/filters (e.g. the top nav,
  // where the pinned orders widget mounts this) hijack position:fixed and
  // would pin the panel to the wrong box.
  return createPortal(
    <div
      className={`fixed inset-0 transition-opacity duration-200 ${open ? 'bg-black/50' : 'bg-black/0'}`}
      style={{ zIndex, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      // Stop propagation so closing a stacked panel doesn't also close the one under it.
      onClick={(e) => { e.stopPropagation(); onClose(); }}
    >
      <div
        className={`fixed inset-y-0 right-0 w-full ${width} bg-white shadow-2xl transform transition-transform duration-200 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        // iOS app: keep the panel content out of the notch / home-indicator zones.
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">{children}</div>
      </div>
    </div>,
    document.body
  );
}

export function Row({ label, value, bold, accent }: { label: string; value: string; bold?: boolean; accent?: string }) {
  return (
    <div className="flex justify-between">
      <span className={`text-[13px] ${bold ? 'font-bold text-gray-900' : 'text-gray-600'}`}>{label}</span>
      <span className={`text-[13px] ${bold ? 'font-bold text-gray-900' : accent || 'text-gray-800'}`}>{value}</span>
    </div>
  );
}

export const fmtMoney = (cents: number) =>
  `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
